import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, COMPANY } from "@/data/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-3 group">
          <span className="h-8 w-8 bg-primary flex items-center justify-center font-display font-bold text-primary-foreground text-lg">
            T
          </span>
          <span className="font-display font-semibold tracking-tight text-lg leading-none">
            TECHNO<span className="text-primary">.</span>
            <span className="block text-[10px] font-mono tracking-[0.3em] text-muted-foreground mt-0.5">
              STAMPING &amp; PRESSING
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors duration-200 relative group"
            >
              {n.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/admin/login"
            data-testid="header-admin-link"
            className="text-xs font-mono tracking-widest text-muted-foreground hover:text-foreground transition-colors"
          >
            ADMIN
          </Link>
          <a
            href="#contact"
            data-testid="header-cta"
            className="bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            Request a Quote
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-foreground"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-5">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="text-base font-medium"
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              {n.label}
            </a>
          ))}
          <Link to="/admin/login" className="text-sm font-mono text-muted-foreground">
            ADMIN LOGIN
          </Link>
          <a href="#contact" onClick={() => setOpen(false)} className="bg-primary text-primary-foreground px-5 py-3 text-center font-semibold">
            Request a Quote
          </a>
        </div>
      )}
    </motion.header>
  );
}
